// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import { arcRows, ArcRows, LanguagePair } from '../../../utils/arcRows';
import { describeError as describeApiError, entryMessage } from '../../../utils/serverErrors';
import { ApiError } from '../../../utils/serverErrors';
import { Decimal } from '../ChangeSetImpact/changeSetImpact';
import { CURRENCIES } from '../Pricing/constants';
import { MATCH_BANDS, REPETITION } from '../ScopingModels/scopingModels';

/**
 * Golden datasets as the portal web service returns them from Pricing: the reference jobs, with
 * the cost each should have, that a change set's cost impact is priced on.
 */

export const GOLDEN_DATASETS_ROUTE = '/settings/goldenDatasets';
export const GOLDEN_DATASETS_PATH = '/goldenDatasets';

export const datasetRoute = (datasetId: string): string =>
  `${GOLDEN_DATASETS_ROUTE}/${encodeURIComponent(datasetId)}`;

export const datasetPath = (datasetId: string): string =>
  `${GOLDEN_DATASETS_PATH}/${encodeURIComponent(datasetId)}`;

export const versionRoute = (datasetId: string, version: number): string =>
  `${datasetRoute(datasetId)}/version/${version}`;

export const versionPath = (datasetId: string, version: number): string =>
  `${datasetPath(datasetId)}/version/${version}`;

export const versionsPath = (datasetId: string): string => `${datasetPath(datasetId)}/versions`;

export const rowsPath = (datasetId: string): string => `${datasetPath(datasetId)}/rows`;

/** What the list is opened with after a delete. */
export interface ListState {
  deletedName: string;
}

export const READ_ONLY_NOTE = 'You can view golden datasets but not change them.';

export type ModelWarningCode =
  | 'pricing_model_missing'
  | 'pricing_model_not_live'
  | 'scoping_model_missing'
  | 'scoping_model_not_live';

/**
 * A problem with one of a dataset's models. Cost impacts skip the dataset while a model is missing;
 * a model not live yet only means its side is costed from a change set that writes it.
 */
export interface ModelWarning {
  code: ModelWarningCode;
  message: string;
}

/** What the write of a version did: created the dataset or replaced its rows. */
export type DatasetChange = 'create' | 'rows';

export const CHANGE_LABELS: Record<DatasetChange, string> = {
  create: 'Created',
  rows: 'Rows replaced',
};

/** One version in a dataset's history. */
export interface GoldenDatasetVersionSummary {
  version: number;
  author: string;
  updatedAt: string;
  change: DatasetChange;
  name: string;
  description: string | null;
  rowCount: number;
}

export interface GoldenDatasetSummary {
  datasetId: string;
  organizationId: string;
  name: string;
  description: string | null;
  // Fixed at create.
  pricingModelId: string;
  pricingModelName: string;
  scopingModelId: string;
  scopingModelName: string;
  version: number;
  // The writer of this version, and when it was written; createdAt is version 1's time.
  author: string;
  updatedAt: string;
  createdAt: string;
  change: DatasetChange;
  rowCount: number;
  // Empty while both models exist and are live.
  modelWarnings: ModelWarning[];
}

export type WordCountBand = 'p0' | 'p50' | 'p75' | 'p85' | 'p95' | 'p100' | 'p101' | 'repetition';

const MATCH_BAND_KEYS: WordCountBand[] = ['p0', 'p50', 'p75', 'p85', 'p95', 'p100', 'p101'];

/** The word-count bands in match order, then repetition, labelled as on the scoping model pages. */
export const WORD_COUNT_BANDS: { key: WordCountBand; label: string }[] = [
  ...MATCH_BAND_KEYS.map((key, index) => ({ key, label: MATCH_BANDS[index].label })),
  { key: 'repetition', label: REPETITION.label },
];

export interface GoldenCost {
  currency: string;
  amount: Decimal;
}

export interface GoldenDatasetRow {
  arc: LanguagePair;
  wordCounts: Record<WordCountBand, number>;
  // The sum of the eight bands.
  totalWords: number;
  goldenCost: GoldenCost;
}

export interface GoldenDataset extends GoldenDatasetSummary {
  // In upload order.
  rows: GoldenDatasetRow[];
}

/** A dataset as Pricing sends it, with each golden cost's amount as a decimal string. */
export const toGoldenDataset = (response: unknown): GoldenDataset => {
  const dataset = response as GoldenDataset;
  return {
    ...dataset,
    rows: dataset.rows.map(row => ({
      ...row,
      goldenCost: { ...row.goldenCost, amount: String(row.goldenCost.amount) },
    })),
  };
};

/** The text a missing model's warning is shown with wherever the dataset is listed or shown. */
export const SKIPPED_IN_IMPACT = 'Skipped in cost impact';

const MISSING_MODEL_CODES: ModelWarningCode[] = ['pricing_model_missing', 'scoping_model_missing'];

/** The warnings that make cost impacts skip the dataset. */
export const skippingWarnings = (dataset: GoldenDatasetSummary): ModelWarning[] =>
  dataset.modelWarnings.filter(warning => MISSING_MODEL_CODES.includes(warning.code));

export type ModelSide = 'pricing' | 'scoping';

export type ModelState = 'live' | 'pending' | 'missing';

/** The state of a dataset's pricing or scoping model, as Pricing reports it in the model warnings. */
export const modelState = (dataset: GoldenDatasetSummary, side: ModelSide): ModelState => {
  const codes = dataset.modelWarnings.map(warning => warning.code);
  if (codes.includes(`${side}_model_missing` as ModelWarningCode)) {
    return 'missing';
  }
  return codes.includes(`${side}_model_not_live` as ModelWarningCode) ? 'pending' : 'live';
};

/** How a model with a live version (Live) or without one yet (Pending) is labelled. */
export const modelStateLabel = (live: boolean): string => (live ? 'Live' : 'Pending');

const ACCESS_DENIED_MESSAGE = 'You do not have permission to view golden datasets.';

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);

export const NAME_MAX = 100;
export const DESCRIPTION_MAX = 500;

/** A dataset's name and description as entered. */
export interface Metadata {
  name: string;
  description: string;
}

export type MetadataField = keyof Metadata;

/** The create fields Pricing's 422s can name. */
export type WriteField = MetadataField | 'pricingModelId' | 'scopingModelId';

const WRITE_FIELDS: WriteField[] = ['name', 'description', 'pricingModelId', 'scopingModelId'];

/** Pricing stores the name trimmed, at most 100 characters; `metadataValid` rejects a blank one. */
export const validateName = (name: string): string | undefined =>
  name.trim().length > NAME_MAX ? `Use at most ${NAME_MAX} characters.` : undefined;

export const validateDescription = (description: string): string | undefined =>
  description.length > DESCRIPTION_MAX ? `Use at most ${DESCRIPTION_MAX} characters.` : undefined;

/** Both are required; Pricing refuses a blank name or description. */
export const metadataValid = (metadata: Metadata): boolean =>
  metadata.name.trim() !== '' &&
  metadata.description.trim() !== '' &&
  validateName(metadata.name) === undefined &&
  validateDescription(metadata.description) === undefined;

/** The dataset that already uses both models; Pricing allows one dataset per pair. */
export const pairTakenBy = (
  pricingModelId: string,
  scopingModelId: string,
  datasets: GoldenDatasetSummary[]
): GoldenDatasetSummary | undefined =>
  datasets.find(
    dataset =>
      dataset.pricingModelId === pricingModelId && dataset.scopingModelId === scopingModelId
  );

export const duplicatePairMessage = (dataset: GoldenDatasetSummary): string =>
  `Golden dataset ${dataset.name} already uses these models`;

/** The body Pricing creates a dataset from. */
export interface CreateRequest {
  name: string;
  description: string;
  pricingModelId: string;
  scopingModelId: string;
  rows: [];
}

/** A dataset with no rows; they are uploaded on its page. */
export const toCreateRequest = (
  metadata: Metadata,
  pricingModelId: string,
  scopingModelId: string
): CreateRequest => ({
  name: metadata.name.trim(),
  description: metadata.description,
  pricingModelId,
  scopingModelId,
  rows: [],
});

/** Deletes the dataset only if it is still at the version shown. */
export const deletePath = ({ datasetId, version }: GoldenDatasetSummary): string =>
  `${datasetPath(datasetId)}?currentLiveVersion=${version}`;

export const CONFLICT_HEADER = 'Could not save the golden dataset';

const WRITE_DENIED_MESSAGE = 'You do not have permission to change golden datasets.';

export const describePricingModelsError = (error: ApiError): string[] =>
  describeApiError(error, 'You do not have permission to list pricing models.');

export const describeScopingModelsError = (error: ApiError): string[] =>
  describeApiError(error, 'You do not have permission to list scoping models.');

export const describeWriteError = (error: ApiError): string[] =>
  describeApiError(error, WRITE_DENIED_MESSAGE);

/** A failed create: errors on its fields, a 409 to reload after, and the rest. */
export interface WriteFailure {
  fieldErrors: Partial<Record<WriteField, string>>;
  // Pricing's messages; a model pair taken, or the dataset changed since it was loaded.
  conflict: string[] | null;
  formErrors: string[];
}

export const NO_FAILURE: WriteFailure = { fieldErrors: {}, conflict: null, formErrors: [] };

const isWriteField = (value: unknown): value is WriteField =>
  WRITE_FIELDS.some(field => field === value);

export const describeWriteFailure = (error: ApiError): WriteFailure => {
  if (error.status === 409) {
    return { ...NO_FAILURE, conflict: describeWriteError(error) };
  }
  const entries = (error.status === 422 && error.details && error.details.errors) || [];
  const fieldErrors: Partial<Record<WriteField, string>> = {};
  const formErrors: string[] = [];
  entries.forEach(entry => {
    const field = (entry.path || [])[0];
    const message = entryMessage(entry) || 'Invalid value';
    if (isWriteField(field)) {
      fieldErrors[field] = message;
    } else {
      formErrors.push(message);
    }
  });
  const placed = formErrors.length > 0 || Object.keys(fieldErrors).length > 0;
  return {
    fieldErrors,
    conflict: null,
    formErrors: placed ? formErrors : describeWriteError(error),
  };
};

/** An uploaded row, keyed by arc like the other per-arc lists. */
export interface UploadedRow {
  languagePair: LanguagePair;
  wordCounts: Record<WordCountBand, number>;
  goldenCost: GoldenCost;
}

type UploadField = WordCountBand | 'goldenCost' | 'currency';

// Pricing refuses fractional word counts rather than rounding them.
const validateWordCount = (value: string): string | undefined =>
  /^\d+$/.test(value) ? undefined : 'Enter a whole number, 0 or more.';

const validateGoldenCost = (value: string): string | undefined =>
  /^\d+(\.\d{1,2})?$/.test(value) && Number(value) > 0
    ? undefined
    : 'Enter an amount greater than 0 with at most 2 decimal places.';

const DATASET_CURRENCIES = [...CURRENCIES].sort();

const uploadRows = arcRows<UploadedRow, UploadField>({
  columns: [
    ...WORD_COUNT_BANDS.map(({ key, label }) => ({
      key,
      header: label,
      validate: validateWordCount,
    })),
    { key: 'goldenCost', header: 'Golden cost', validate: validateGoldenCost },
    {
      key: 'currency',
      header: 'Currency',
      // Uploads may write a currency in any case.
      normalize: (value): string => value.toUpperCase(),
      validate: (value): string | undefined =>
        DATASET_CURRENCIES.includes(value) ? undefined : `Use ${DATASET_CURRENCIES.join(' or ')}.`,
    },
  ],
  valuesOf: ({ wordCounts, goldenCost }) => {
    const values = { goldenCost: goldenCost.amount, currency: goldenCost.currency };
    WORD_COUNT_BANDS.forEach(({ key }) => {
      values[key] = String(wordCounts[key]);
    });
    return values as Record<UploadField, string>;
  },
  itemOf: (languagePair, values) => {
    const wordCounts = {} as Record<WordCountBand, number>;
    WORD_COUNT_BANDS.forEach(({ key }) => {
      wordCounts[key] = Number(values[key]);
    });
    return {
      languagePair,
      wordCounts,
      goldenCost: { amount: values.goldenCost, currency: values.currency },
    };
  },
  collection: 'rows',
  rejectedMessage: 'Pricing rejected the rows.',
  accessDeniedMessage: WRITE_DENIED_MESSAGE,
});

const NO_WORDS_MESSAGE = 'A row must have at least one word.';

// Only rows whose bands are all valid are checked, so an empty row is not flagged twice.
const hasNoWords = (row: Record<WordCountBand, string>): boolean =>
  WORD_COUNT_BANDS.every(({ key }) => /^0+$/.test(row[key]));

/** The upload columns, which are also the template's, with Pricing's check that a row has words. */
export const datasetRows: ArcRows<UploadedRow, UploadField> = {
  ...uploadRows,
  validateRows: (rows, locales) =>
    uploadRows
      .validateRows(rows, locales)
      .map((errors, index) =>
        hasNoWords(rows[index]) && !errors.row ? { ...errors, row: NO_WORDS_MESSAGE } : errors
      ),
};

export const UPLOAD_CONFLICT_HEADER = 'The dataset changed since you loaded it; reload';

/** A row as Pricing reads it in a replace; Pricing sums the bands itself. */
export interface RowRequest {
  arc: LanguagePair;
  wordCounts: Record<WordCountBand, number>;
  goldenCost: { currency: string; amount: number };
}

export interface ReplaceRowsRequest {
  currentLiveVersion: number;
  rows: RowRequest[];
}

/** Replaces every row, only if the dataset is still at the version shown. */
export const toReplaceRowsRequest = (
  currentLiveVersion: number,
  rows: UploadedRow[]
): ReplaceRowsRequest => ({
  currentLiveVersion,
  rows: rows.map(({ languagePair, wordCounts, goldenCost }) => ({
    arc: languagePair,
    wordCounts,
    // The portal web service reads the amount as a JSON number, which keeps the digits of any amount
    // of up to 15 significant digits; Pricing sets no upper bound, so a larger one may be rounded.
    goldenCost: { currency: goldenCost.currency, amount: Number(goldenCost.amount) },
  })),
});
